import React, { useState, useEffect } from "react";
import { 
  FiSettings, 
  FiMoon, 
  FiBell, 
  FiGlobe, 
  FiMail, 
  FiSave,
  FiCheck,
  FiRefreshCw,
  FiShield,
  FiEye,
  FiEyeOff
} from "react-icons/fi";

const SettingsPanel = () => {
  const [settings, setSettings] = useState({
    darkMode: false,
    notifications: true,
    emailNotifications: true,
    bookingAlerts: true,
    language: "en",
    autoRefresh: false,
    showSensitiveData: false,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem("adminSettings");
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error("Error loading settings:", error);
      }
    }
    setIsInitialLoad(false);
  }, []);

  // Track changes (but not on initial load)
  useEffect(() => {
    if (!isInitialLoad) {
      setHasChanges(true);
      setSaved(false);
    }
  }, [settings, isInitialLoad]);

  const handleToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleLanguageChange = (value) => {
    setSettings(prev => ({
      ...prev,
      language: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save to localStorage
      localStorage.setItem("adminSettings", JSON.stringify(settings));
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setSaved(true);
      setHasChanges(false);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSaved(false);
      }, 3000);
    } catch (error) {
      console.error("Error saving settings:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm("Are you sure you want to reset all settings to default?")) {
      const defaultSettings = {
        darkMode: false,
        notifications: true,
        emailNotifications: true,
        bookingAlerts: true,
        language: "en",
        autoRefresh: false,
        showSensitiveData: false,
      };
      setSettings(defaultSettings);
      localStorage.setItem("adminSettings", JSON.stringify(defaultSettings));
      setHasChanges(false);
      setSaved(false);
    }
  };

  return (
    <div className="ui-mdc-settings-panel">
      <div className="ui-mdc-settings-panel-content">
        <div className="ui-mdc-settings-panel-header">
          <div className="ui-mdc-settings-panel-icon">
            <FiSettings />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="ui-mdc-settings-panel-title">Settings</h2>
            <p className="ui-mdc-settings-panel-subtitle">
              Manage your preferences and dashboard settings
            </p>
          </div>
        </div>

        <div className="ui-mdc-settings-panel-body">
          {/* Display Preferences */}
          <div className="ui-mdc-settings-section">
            <h3 className="ui-mdc-settings-section-title">Display Preferences</h3>
            
            <div className="ui-mdc-settings-form-group">
              <label className="ui-mdc-settings-checkbox-label">
                <input
                  type="checkbox"
                  className="ui-mdc-settings-checkbox-input"
                  checked={settings.darkMode}
                  onChange={() => handleToggle("darkMode")}
                />
                <span className="ui-mdc-settings-checkbox-decor">
                  {settings.darkMode && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 6l3 3 5-5" />
                    </svg>
                  )}
                </span>
                <div className="ui-mdc-settings-checkbox-content">
                  <div className="ui-mdc-settings-checkbox-icon">
                    <FiMoon />
                  </div>
                  <div>
                    <span className="ui-mdc-settings-checkbox-title">Dark Mode</span>
                    <span className="ui-mdc-settings-checkbox-description">Enable dark theme for better visibility</span>
                  </div>
                </div>
              </label>
            </div>

            <div className="ui-mdc-settings-form-group">
              <label className="ui-mdc-settings-checkbox-label">
                <input
                  type="checkbox"
                  className="ui-mdc-settings-checkbox-input"
                  checked={settings.autoRefresh}
                  onChange={() => handleToggle("autoRefresh")}
                />
                <span className="ui-mdc-settings-checkbox-decor">
                  {settings.autoRefresh && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 6l3 3 5-5" />
                    </svg>
                  )}
                </span>
                <div className="ui-mdc-settings-checkbox-content">
                  <div className="ui-mdc-settings-checkbox-icon">
                    <FiRefreshCw />
                  </div>
                  <div>
                    <span className="ui-mdc-settings-checkbox-title">Auto Refresh</span>
                    <span className="ui-mdc-settings-checkbox-description">Automatically refresh booking data</span>
                  </div>
                </div>
              </label>
            </div>

            <div className="ui-mdc-settings-form-group">
              <label className="ui-mdc-settings-checkbox-label">
                <input
                  type="checkbox"
                  className="ui-mdc-settings-checkbox-input"
                  checked={settings.showSensitiveData}
                  onChange={() => handleToggle("showSensitiveData")}
                />
                <span className="ui-mdc-settings-checkbox-decor">
                  {settings.showSensitiveData && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 6l3 3 5-5" />
                    </svg>
                  )}
                </span>
                <div className="ui-mdc-settings-checkbox-content">
                  <div className="ui-mdc-settings-checkbox-icon">
                    {settings.showSensitiveData ? <FiEye /> : <FiEyeOff />}
                  </div>
                  <div>
                    <span className="ui-mdc-settings-checkbox-title">Show Sensitive Data</span>
                    <span className="ui-mdc-settings-checkbox-description">Display full customer information</span>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Notifications */}
          <div className="ui-mdc-settings-section">
            <h3 className="ui-mdc-settings-section-title">Notifications</h3>
            
            <div className="ui-mdc-settings-form-group">
              <label className="ui-mdc-settings-checkbox-label">
                <input
                  type="checkbox"
                  className="ui-mdc-settings-checkbox-input"
                  checked={settings.notifications}
                  onChange={() => handleToggle("notifications")}
                />
                <span className="ui-mdc-settings-checkbox-decor">
                  {settings.notifications && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 6l3 3 5-5" />
                    </svg>
                  )}
                </span>
                <div className="ui-mdc-settings-checkbox-content">
                  <div className="ui-mdc-settings-checkbox-icon">
                    <FiBell />
                  </div>
                  <div>
                    <span className="ui-mdc-settings-checkbox-title">Browser Notifications</span>
                    <span className="ui-mdc-settings-checkbox-description">Receive browser push notifications</span>
                  </div>
                </div>
              </label>
            </div>

            <div className="ui-mdc-settings-form-group">
              <label className="ui-mdc-settings-checkbox-label">
                <input
                  type="checkbox"
                  className="ui-mdc-settings-checkbox-input"
                  checked={settings.emailNotifications}
                  onChange={() => handleToggle("emailNotifications")}
                />
                <span className="ui-mdc-settings-checkbox-decor">
                  {settings.emailNotifications && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 6l3 3 5-5" />
                    </svg>
                  )}
                </span>
                <div className="ui-mdc-settings-checkbox-content">
                  <div className="ui-mdc-settings-checkbox-icon">
                    <FiMail />
                  </div>
                  <div>
                    <span className="ui-mdc-settings-checkbox-title">Email Notifications</span>
                    <span className="ui-mdc-settings-checkbox-description">Get notified via email</span>
                  </div>
                </div>
              </label>
            </div>

            <div className="ui-mdc-settings-form-group">
              <label className="ui-mdc-settings-checkbox-label">
                <input
                  type="checkbox"
                  className="ui-mdc-settings-checkbox-input"
                  checked={settings.bookingAlerts}
                  onChange={() => handleToggle("bookingAlerts")}
                />
                <span className="ui-mdc-settings-checkbox-decor">
                  {settings.bookingAlerts && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 6l3 3 5-5" />
                    </svg>
                  )}
                </span>
                <div className="ui-mdc-settings-checkbox-content">
                  <div className="ui-mdc-settings-checkbox-icon">
                    <FiShield />
                  </div>
                  <div>
                    <span className="ui-mdc-settings-checkbox-title">Booking Alerts</span>
                    <span className="ui-mdc-settings-checkbox-description">Alert for new bookings and updates</span>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Language */}
          <div className="ui-mdc-settings-section">
            <h3 className="ui-mdc-settings-section-title">Language & Region</h3>
            
            <div className="ui-mdc-settings-form-group">
              <label className="ui-mdc-settings-select-label">
                <div className="ui-mdc-settings-select-icon">
                  <FiGlobe />
                </div>
                <span className="ui-mdc-settings-select-text">Interface Language</span>
              </label>
              <div className="ui-mdc-settings-select-wrapper">
                <select
                  value={settings.language}
                  onChange={e => handleLanguageChange(e.target.value)}
                  className="ui-mdc-settings-select"
                >
                  <option value="en">English</option>
                  <option value="si">සිංහල (Sinhala)</option>
                  <option value="ta">தமிழ் (Tamil)</option>
                  <option value="fr">Français (French)</option>
                  <option value="es">Español (Spanish)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="ui-mdc-settings-actions">
          <button
            type="button"
            onClick={handleReset}
            className="ui-mdc-confirm-button ui-mdc-confirm-button--secondary ui-mdc-settings-action-button"
          >
            <FiRefreshCw className="ui-mdc-settings-button-icon" />
            <span>Reset</span>
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || (!hasChanges && !saved)}
            className={`ui-mdc-confirm-button ui-mdc-settings-action-button ui-mdc-settings-action-button--primary ${
              saved ? "ui-mdc-settings-action-button--success" : ""
            } ${(!hasChanges && !saved) ? "ui-mdc-settings-action-button--disabled" : ""}`}
          >
            {saving ? (
              <>
                <FiRefreshCw className="ui-mdc-settings-button-icon ui-mdc-settings-button-icon--spinning" />
                <span>Saving...</span>
              </>
            ) : saved ? (
              <>
                <FiCheck className="ui-mdc-settings-button-icon" />
                <span>Saved!</span>
              </>
            ) : (
              <>
                <FiSave className="ui-mdc-settings-button-icon" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;